/**
 * AppICEMAP — UserLocationDot
 * Gold teardrop pin following the user's GPS position.
 *
 * Uses navigator.geolocation.watchPosition for live tracking. The pin and its
 * card only exist once a real fix arrives — nothing is drawn while the
 * permission prompt is pending or if the user denies it.
 */

import L from "leaflet";
import { dispatch } from "../../store/index.js";
import { reverseGeocode } from "../../services/geocode.js";
import { isInUS } from "../../utils/geo.js";
import { fitPopupToMap } from "./popupFit.js";
import { show as showToast } from "../shared/Toast.js";

/** Teardrop pin, anchored at its tip. Colours come from map-viewer.css. */
const PIN_ICON = L.divIcon({
	className: "user-location__pin",
	iconSize: [28, 38],
	iconAnchor: [14, 38],
	tooltipAnchor: [0, -38],
	html: `
		<svg width="28" height="38" viewBox="0 0 28 38" aria-hidden="true">
			<path class="user-location__pin-body"
				d="M14 0.8C6.6 0.8 0.8 6.6 0.8 14c0 8.4 10.6 20.6 12.4 22.6a1.1 1.1 0 0 0 1.6 0C16.6 34.6 27.2 22.4 27.2 14 27.2 6.6 21.4 0.8 14 0.8z" />
			<circle class="user-location__pin-core" cx="14" cy="13.6" r="4.4" />
		</svg>
	`,
});

/** @type {L.Marker | null} */
let userDot = null;

/** @type {number | null} */
let watchId = null;

/** @type {L.Map | null} */
let mapInstance = null;

/** @type {boolean} the card opens once on first fix; respect a manual close */
let cardDismissed = false;

/** @type {string | null} last coord we reverse-geocoded, to avoid re-querying */
let geocodedKey = null;

/** @type {{name: string, area: string} | null} last resolved place label */
let placeLabel = null;

/** @type {string | null} last painted card markup, to skip no-op repaints */
let lastCardHtml = null;

/** @type {[number, number] | null} last position published to the store */
let lastPublished = null;

/** @type {boolean} log the "outside the country" notice only once */
let outsideNotified = false;

/** @type {boolean} avisar del problema de ubicación una sola vez por sesión */
let errorNotified = false;

/** Metres the user must move before the position is published to the store */
const PUBLISH_THRESHOLD_M = 10;

/**
 * Has the user moved far enough from the last published position to be worth
 * telling the rest of the app? Equirectangular approximation — plenty at this
 * scale and far cheaper than haversine on every fix.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {boolean}
 */
function movedSignificantly(lat, lng) {
	if (!lastPublished) return true;

	const [prevLat, prevLng] = lastPublished;
	const metresPerDegLat = 111_320;
	const dLat = (lat - prevLat) * metresPerDegLat;
	const dLng =
		(lng - prevLng) * metresPerDegLat * Math.cos((lat * Math.PI) / 180);

	return Math.hypot(dLat, dLng) >= PUBLISH_THRESHOLD_M;
}

/**
 * Start user location tracking on the map.
 *
 * The pin and its card are NOT created up front: they appear the moment the
 * first real GPS fix arrives, so nothing is drawn at a fake fallback position
 * while the permission prompt is still open (or if it is denied).
 *
 * @param {L.Map} map - Leaflet map instance
 */
export function initUserLocation(map) {
	mapInstance = map;
	startWatching();
}

/**
 * Create the pin and its card on the first fix.
 * @param {[number, number]} latlng
 */
function ensurePin(latlng) {
	if (userDot || !mapInstance) return;

	userDot = L.marker(latlng, {
		icon: PIN_ICON,
		keyboard: false,
		zIndexOffset: 1000,
		interactive: true,
	})
		.addTo(mapInstance)
		.bindPopup(
			L.popup({
				className: "popup-card-wrapper user-location__card-wrapper",
				maxWidth: 260,
				minWidth: 200,
				offset: [0, -34],
				autoClose: false,
				closeOnClick: false,
				autoPan: false,
			}),
		);

	// The card is visible by default; if the user closes it, keep it closed
	userDot.on("popupclose", () => {
		cardDismissed = true;
	});

	// La tarjeta se acota al mapa, pero SIN mover el mapa: al arrancar se abre
	// sola justo después de centrarte, y desplazarla te sacaba del centro —
	// parecía que la app ya no te enfocaba.
	userDot.on("popupopen", (e) => fitPopupToMap(mapInstance, e.popup));
}

/**
 * Stop geolocation tracking and remove markers.
 */
export function destroyUserLocation() {
	stopWatching();

	if (userDot && mapInstance) {
		mapInstance.removeLayer(userDot);
		userDot = null;
	}
	mapInstance = null;
	cardDismissed = false;
	geocodedKey = null;
	placeLabel = null;
	lastCardHtml = null;
	lastPublished = null;
	outsideNotified = false;
	errorNotified = false;
}

/**
 * Get the last known user position.
 * @returns {[number, number] | null}
 */
export function getUserPosition() {
	if (userDot) {
		const latlng = userDot.getLatLng();
		return [latlng.lat, latlng.lng];
	}
	return null;
}

/* ── Internal ──────────────────────────────────────────────────────────── */

function startWatching() {
	if (!navigator.geolocation) {
		console.warn("[UserLocation] Geolocation not supported");
		return;
	}

	// Get initial position quickly
	navigator.geolocation.getCurrentPosition(
		(pos) => updatePosition(pos),
		(err) => handleError(err),
		{ enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
	);

	// Watch for changes
	watchId = navigator.geolocation.watchPosition(
		(pos) => updatePosition(pos),
		(err) => handleError(err),
		{ enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
	);
}

function stopWatching() {
	if (watchId !== null) {
		navigator.geolocation.clearWatch(watchId);
		watchId = null;
	}
}

/**
 * @param {GeolocationPosition} pos
 */
function updatePosition(pos) {
	const { latitude, longitude } = pos.coords;
	const latlng = [latitude, longitude];

	// The app covers the US only: a position abroad is not drawn and the map is
	// not recentred on it, or the user would be dropped outside the coverage
	// area with an empty list and no explanation.
	if (!isInUS(latitude, longitude)) {
		if (!outsideNotified) {
			outsideNotified = true;
			console.info("[UserLocation] Posición fuera de EE. UU.: no se muestra");
		}
		return;
	}

	// First real fix → the pin and its card appear now
	ensurePin(latlng);

	if (userDot) {
		userDot.setLatLng(latlng);
		refreshCard(latitude, longitude, pos.timestamp);
		if (!cardDismissed && !userDot.isPopupOpen()) userDot.openPopup();
	}

	// Store in global state — but only when the position actually moved.
	// watchPosition fires every second or so and GPS jitters by a few metres
	// even when standing still; every dispatch wakes each "ui" subscriber, so
	// publishing that noise makes the whole app churn for nothing.
	if (movedSignificantly(latitude, longitude)) {
		lastPublished = latlng;
		dispatch("SET_UI", { userLocation: latlng });
	}

	// Centrar en el primer fix. SIN animar: el salto desde el centro por
	// defecto (San Francisco) es de miles de km y la animación se quedaba a
	// medias si algo la interrumpía, dejando el mapa en mitad de la nada.
	if (mapInstance && !mapInstance._userCentered) {
		mapInstance.setView(latlng, 15, { animate: false });
		mapInstance._userCentered = true;
	}
}

/* ── Location card ─────────────────────────────────────────────────────── */

/**
 * Build the card HTML shown above the pin.
 * @param {string} title - Place name
 * @param {string} subtitle - "Ciudad, País" or coordinates
 * @param {number} timestamp - Epoch ms of the GPS fix being displayed
 * @returns {string}
 */
function buildCard(title, subtitle, timestamp) {
	const now = new Date(timestamp);
	const date = now
		.toLocaleDateString("es-MX", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		})
		.replace(/\//g, " · ");
	const time = now.toLocaleTimeString("es-MX", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});

	return `
		<div class="popup-card user-location__card" role="dialog" aria-label="Tu ubicación">
			<h4 class="popup-card__title">${escapeHtml(title)}</h4>
			${subtitle ? `<p class="user-location__card-area">${escapeHtml(subtitle)}</p>` : ""}
			<p class="popup-card__meta">${date} — ${time}H</p>
		</div>
	`;
}

/**
 * Paint the card with the last known place label and the given fix time.
 * Skips the DOM write when nothing actually changed, so a stream of GPS
 * updates within the same minute and place doesn't repaint the popup.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {number} timestamp - Epoch ms of the GPS fix
 */
function renderCard(lat, lng, timestamp) {
	if (!userDot) return;

	// 4 decimals ≈ 11 m: precise enough to read, coarse enough that GPS jitter
	// doesn't rewrite the card every second while standing still.
	const coords = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
	const html = placeLabel
		? buildCard(placeLabel.name, placeLabel.area || coords, timestamp)
		: buildCard("Tu ubicación", coords, timestamp);

	if (html === lastCardHtml) return;
	lastCardHtml = html;
	userDot.setPopupContent(html);
}

/**
 * Refresh the card for a new fix, reverse geocoding when the user has actually
 * moved. The time line updates immediately; the place name fills in when the
 * lookup resolves (or stays as coordinates if it fails).
 *
 * @param {number} lat
 * @param {number} lng
 * @param {number} timestamp - Epoch ms of the GPS fix
 */
async function refreshCard(lat, lng, timestamp) {
	renderCard(lat, lng, timestamp);

	const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
	if (key === geocodedKey) return;
	geocodedKey = key;

	const label = await reverseGeocode(lat, lng);

	// The marker may have been destroyed while the lookup was in flight
	if (!userDot || !label) return;

	placeLabel = label;
	renderCard(lat, lng, timestamp);
}

/**
 * Escape HTML entities to prevent XSS from geocoder-supplied names.
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str;
	return div.innerHTML;
}

/**
 * @param {GeolocationPositionError} err
 */
function handleError(err) {
	console.warn("[UserLocation] Geolocation error:", err.message);

	// Un solo aviso por sesión: repetirlo con cada reintento del watch sería
	// insoportable.
	if (errorNotified) return;
	errorNotified = true;

	// PERMISSION_DENIED = 1. El navegador RECUERDA la decisión y no vuelve a
	// preguntar, así que sin este aviso la app parece estar rota: no te centra,
	// no hay pin y no aparece ningún permiso que aceptar.
	if (err.code === 1) {
		// Más tiempo del habitual: pide una acción concreta y hay que leerlo
		showToast(
			"Ubicación bloqueada. Actívala en el candado de la barra de direcciones para ver lo que hay cerca de ti.",
			"warning",
			9000,
		);
		return;
	}

	if (err.code === 3) {
		showToast("No se pudo obtener tu ubicación (señal débil).", "warning", 6000);
		return;
	}

	showToast("No se pudo obtener tu ubicación.", "warning");
}
